/**
 * Partner clinic finder — slim modal owning postcode search + OpenStreetMap.
 *
 * Reads clinic data from <script id="partner-clinic-data" type="application/json">
 * (server-rendered from the partner_clinic metaobject in Liquid).
 *
 * Geocodes UK postcodes via postcodes.io (no key required, no rate limit).
 * Loads Leaflet lazily on first modal open so non-users pay zero perf cost.
 *
 * On clinic click: writes hidden inputs to the product form
 *   properties[Partner Clinic] = "Name, Address, Postcode" (visible)
 *   properties[_Partner Clinic Postcode] = postcode (hidden via underscore prefix)
 * then closes the modal and ensures the partner-clinic radio is selected.
 */
(function () {
  if (window.partnerClinicFindInitialized) return;
  window.partnerClinicFindInitialized = true;

  var POSTCODES_API = 'https://api.postcodes.io/postcodes/';
  var LEAFLET_CSS = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
  var LEAFLET_JS = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
  var MAX_RESULTS = 25;
  var DEFAULT_CENTER = [54.5, -2.5]; // UK approx
  var DEFAULT_ZOOM = 6;

  var clinics = [];
  var map = null;
  var markersLayer = null;
  var leafletReady = null;
  var hasRenderedInitial = false;

  function loadClinicData() {
    var dataEl = document.getElementById('partner-clinic-data');
    if (!dataEl) return [];
    try {
      var parsed = JSON.parse(dataEl.textContent || '[]');
      // Liquid emits number_decimal fields as JSON strings ("51.49"), coerce to Number.
      return parsed
        .map(function (c) {
          if (!c) return null;
          var lat = typeof c.lat === 'number' ? c.lat : parseFloat(c.lat);
          var lng = typeof c.lng === 'number' ? c.lng : parseFloat(c.lng);
          if (isNaN(lat) || isNaN(lng)) return null;
          return Object.assign({}, c, { lat: lat, lng: lng });
        })
        .filter(function (c) { return c && c.name; });
    } catch (err) {
      console.error('[partner-clinic-find] failed to parse clinic data:', err);
      return [];
    }
  }

  function ensureLeaflet() {
    if (window.L) return Promise.resolve();
    if (leafletReady) return leafletReady;
    leafletReady = new Promise(function (resolve, reject) {
      var cssLink = document.createElement('link');
      cssLink.rel = 'stylesheet';
      cssLink.href = LEAFLET_CSS;
      document.head.appendChild(cssLink);
      var script = document.createElement('script');
      script.src = LEAFLET_JS;
      script.onload = function () { resolve(); };
      script.onerror = function (err) { reject(err); };
      document.head.appendChild(script);
    });
    return leafletReady;
  }

  function distanceKm(lat1, lng1, lat2, lng2) {
    var R = 6371;
    var toRad = function (d) { return d * Math.PI / 180; };
    var dLat = toRad(lat2 - lat1);
    var dLng = toRad(lng2 - lng1);
    var a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
            Math.sin(dLng / 2) * Math.sin(dLng / 2);
    return 2 * R * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }

  function geocodePostcode(postcode) {
    var cleaned = String(postcode || '').replace(/\s+/g, '').toUpperCase();
    if (!cleaned) return Promise.reject(new Error('Postcode is required.'));
    var url = POSTCODES_API + encodeURIComponent(cleaned);
    return fetch(url).then(function (res) {
      if (res.status === 404) throw new Error('Postcode not found. Please check and try again.');
      if (!res.ok) throw new Error('Could not look up that postcode. Please try again.');
      return res.json();
    }).then(function (json) {
      if (!json || !json.result) throw new Error('Postcode not found.');
      return { lat: json.result.latitude, lng: json.result.longitude, postcode: json.result.postcode };
    });
  }

  function escapeHtml(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (ch) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch];
    });
  }

  function getModal() {
    return document.querySelector('[data-partner-clinic-modal]');
  }

  function getProductForm() {
    return document.querySelector('product-form form, form[action$="/cart/add"]') ||
           document.querySelector('.product-form');
  }

  function ensureProperty(form, key, value) {
    if (!form) return;
    var selector = 'input[name="properties[' + key + ']"]';
    var field = form.querySelector(selector);
    if (!value) {
      if (field) field.remove();
      return;
    }
    if (!field) {
      field = document.createElement('input');
      field.type = 'hidden';
      field.name = 'properties[' + key + ']';
      form.appendChild(field);
    }
    field.value = value;
  }

  function initMap(container) {
    return ensureLeaflet().then(function () {
      if (map) {
        setTimeout(function () { map.invalidateSize(); }, 50);
        return;
      }
      map = L.map(container, { zoomControl: true, attributionControl: true }).setView(DEFAULT_CENTER, DEFAULT_ZOOM);
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; OpenStreetMap contributors',
        maxZoom: 19
      }).addTo(map);
      markersLayer = L.layerGroup().addTo(map);
      setTimeout(function () { map.invalidateSize(); }, 50);
    });
  }

  function renderResults(items, listEl) {
    if (!listEl) return;
    listEl.innerHTML = '';
    if (!items.length) {
      var empty = document.createElement('li');
      empty.className = 'partner-clinic-empty';
      empty.textContent = clinics.length ? 'No clinics found near that postcode.' : 'No partner clinics configured yet.';
      listEl.appendChild(empty);
      return;
    }
    items.forEach(function (c) {
      var li = document.createElement('li');
      li.className = 'partner-clinic-result';
      var distanceText = (typeof c._distanceKm === 'number') ?
        '<div class="partner-clinic-result-distance">' + c._distanceKm.toFixed(1) + ' km away</div>' : '';
      var phoneText = c.phone ?
        '<div class="partner-clinic-result-meta">' + escapeHtml(c.phone) + '</div>' : '';
      li.innerHTML =
        '<div class="partner-clinic-result-title">' + escapeHtml(c.name) + '</div>' +
        '<div class="partner-clinic-result-meta">' + escapeHtml(c.address || '') + (c.postcode ? ', ' + escapeHtml(c.postcode) : '') + '</div>' +
        phoneText +
        distanceText +
        '<button type="button" class="partner-clinic-result-button">Choose this clinic</button>';
      li.addEventListener('click', function () { selectClinic(c); });
      listEl.appendChild(li);
    });
  }

  function renderMarkers(items, searchPoint) {
    if (!markersLayer || !window.L) return;
    markersLayer.clearLayers();
    var points = items.map(function (c) { return [c.lat, c.lng]; });
    if (searchPoint) {
      var here = L.circleMarker([searchPoint.lat, searchPoint.lng], {
        radius: 8, fillColor: '#2563eb', color: '#fff', weight: 2, fillOpacity: 0.95
      });
      here.bindTooltip('You searched here', { permanent: false });
      here.addTo(markersLayer);
      points.push([searchPoint.lat, searchPoint.lng]);
    }
    items.forEach(function (c) {
      var m = L.marker([c.lat, c.lng]);
      // Popup-only on click: shows clinic info but does NOT select.
      // Selection happens explicitly via the list "Choose this clinic" button.
      m.bindPopup(
        '<strong>' + escapeHtml(c.name) + '</strong><br>' +
        escapeHtml(c.address || '') + (c.postcode ? '<br>' + escapeHtml(c.postcode) : '')
      );
      m.addTo(markersLayer);
    });
    if (points.length > 0 && map) {
      var bounds = L.latLngBounds(points);
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 13 });
    }
  }

  function selectClinic(clinic) {
    var form = getProductForm();
    if (form) {
      var display = clinic.name + ', ' + (clinic.address || '');
      if (clinic.postcode) display += ', ' + clinic.postcode;
      ensureProperty(form, 'Partner Clinic', display);
      ensureProperty(form, '_Partner Clinic Postcode', clinic.postcode || '');
    }
    var summary = document.querySelector('[data-partner-clinic-selected-summary]');
    if (summary) {
      summary.hidden = false;
      summary.innerHTML = '<strong>Selected:</strong> ' + escapeHtml(clinic.name) +
        (clinic.postcode ? ' &middot; ' + escapeHtml(clinic.postcode) : '');
    }
    var radio = document.querySelector('input[type="radio"][name="sample_collection"][value="partner-clinic"]');
    if (radio) {
      radio.checked = true;
      radio.dispatchEvent(new Event('change', { bubbles: true }));
    }
    // Once a clinic has been chosen, relabel the open-modal trigger to indicate it can be used to change.
    var triggerBtns = document.querySelectorAll('[data-partner-clinic-option] [data-partner-clinic-open]');
    triggerBtns.forEach(function (b) { b.textContent = 'Change my clinic'; });
    closeModal();
  }

  function openModal() {
    var modal = getModal();
    if (!modal) return;
    modal.hidden = false;
    modal.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    var overlay = document.querySelector('[data-partner-clinic-overlay]');
    if (overlay) overlay.hidden = false;
    var mapEl = modal.querySelector('[data-partner-clinic-map]');
    if (mapEl) initMap(mapEl);
    if (!hasRenderedInitial) {
      var listEl = modal.querySelector('[data-partner-clinic-results]');
      var initial = clinics.slice(0, MAX_RESULTS);
      renderResults(initial, listEl);
      ensureLeaflet().then(function () { renderMarkers(initial); }).catch(function () {});
      hasRenderedInitial = true;
    }
    var input = modal.querySelector('[data-partner-clinic-postcode]');
    if (input) setTimeout(function () { try { input.focus(); } catch (e) {} }, 100);
  }

  function closeModal() {
    var modal = getModal();
    if (!modal) return;
    modal.hidden = true;
    modal.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    var overlay = document.querySelector('[data-partner-clinic-overlay]');
    if (overlay) overlay.hidden = true;
  }

  function handleSearch() {
    var modal = getModal();
    if (!modal) return;
    var input = modal.querySelector('[data-partner-clinic-postcode]');
    var status = modal.querySelector('[data-partner-clinic-status]');
    var listEl = modal.querySelector('[data-partner-clinic-results]');
    var postcode = input ? input.value.trim() : '';
    if (!postcode) {
      if (listEl) renderResults(clinics.slice(0, MAX_RESULTS), listEl);
      if (status) status.textContent = '';
      ensureLeaflet().then(function () { renderMarkers(clinics.slice(0, MAX_RESULTS)); }).catch(function () {});
      return;
    }
    if (status) status.textContent = 'Searching\u2026';
    geocodePostcode(postcode).then(function (point) {
      var sorted = clinics
        .map(function (c) {
          var copy = {};
          for (var k in c) if (Object.prototype.hasOwnProperty.call(c, k)) copy[k] = c[k];
          copy._distanceKm = distanceKm(point.lat, point.lng, c.lat, c.lng);
          return copy;
        })
        .sort(function (a, b) { return a._distanceKm - b._distanceKm; })
        .slice(0, MAX_RESULTS);
      if (listEl) renderResults(sorted, listEl);
      if (status) {
        var pluralised = sorted.length === 1 ? 'clinic' : 'clinics';
        status.textContent = 'Showing ' + sorted.length + ' ' + pluralised + ' nearest to ' + point.postcode;
      }
      return ensureLeaflet().then(function () { renderMarkers(sorted, point); });
    }).catch(function (err) {
      if (status) status.textContent = (err && err.message) ? err.message : 'Search failed.';
    });
  }

  function init() {
    clinics = loadClinicData();
    document.addEventListener('click', function (event) {
      var trigger = event.target.closest('[data-partner-clinic-open]');
      if (!trigger) return;
      event.preventDefault();
      event.stopPropagation();
      openModal();
    }, true);
    document.addEventListener('click', function (event) {
      if (event.target.closest('[data-partner-clinic-close]') || event.target === document.querySelector('[data-partner-clinic-overlay]')) {
        event.preventDefault();
        closeModal();
      }
    });
    document.addEventListener('keyup', function (event) {
      if (event.key !== 'Escape') return;
      var modal = getModal();
      if (modal && !modal.hidden) closeModal();
    });
    document.addEventListener('submit', function (event) {
      var form = event.target.closest('[data-partner-clinic-search-form]');
      if (!form) return;
      event.preventDefault();
      event.stopPropagation();
      handleSearch();
    });
    document.addEventListener('click', function (event) {
      var btn = event.target.closest('[data-partner-clinic-search-btn]');
      if (!btn) return;
      event.preventDefault();
      handleSearch();
    });
    window.partnerClinicModal = { open: openModal, close: closeModal };
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
